





















































































































import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { NavigationGuardNext, Route } from "vue-router";

/**
 * Utils
 */
import { teamUrl } from "@/utils/url-helpers";

/**
 * Components
 */
import GroupCard from "@/components/_global/GroupCard.vue";
import RosterSelector from "@/components/_global/RosterSelector.vue";

/**
 * API
 */
import rostersAPI from "@/api/rosters";

/**
 * Types
 */
import roles from "@/config/wow/roles";
import { normalizeString } from "@/utils/string-helpers";
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";
import DataFetcher from "@/components/_global/DataFetcher.vue";

/**
 * VueX Modules
 */
const teamModule = namespace("team");

/**
 * Roster's list page.
 */
@Component({
	components: {
		DataFetcher,
		GroupCard,
		RosterSelector
	},
	beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
		const that = this as RostersEditorPage;
		if (!that.hasUnsavedChanges) {
			return next();
		}
		const answer = window.confirm(that.$t("global.alert_leave_page") as string);
		if (answer) {
			next();
		}
		else {
			next(false);
		}
	}
})
export default class RostersEditorPage extends Vue {
	/// Injections
	teamUrl = teamUrl;

	/// Attributes
	isLoading = false;
	rosters: any[] | null = null;
	hasUnsavedChanges = false;
	allCharacters: any[] | null = null;
	activeRosterId: any | null = null;
	// Filters
	searchField = "";

	/// VueX Modules
	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse;

	get activeRoster() {
		if (!this.rosters) {
			return null;
		}
		return this.rosters.find(x => x.id == this.activeRosterId);
	}

	get displayedTeamCharacters() {
		if (!this.allCharacters) {
			return null;
		}
		const searchField = normalizeString(this.searchField);
		return this.allCharacters.filter(c =>
			c.name && normalizeString(c.name).includes(searchField));
	}


	get canSave() {
		return !this.isLoading && this.hasUnsavedChanges;
	}

	get canAddOrRemoveRoster() {
		return !this.isLoading && !this.hasUnsavedChanges;
	}

	/// Methods
	async created() {
	}

	async fetch() {
		await this.fetchRosters();
		this.allCharacters = await rostersAPI.fetchTeamCharacters(this.currentTeam.id);

		if (this.rosters != null && this.rosters.length > 0) {
			this.activeRosterId = this.rosters[0].id;
		}
	}

	async fetchRosters() {
		this.rosters = await rostersAPI.fetchRosters(this.currentTeam.id);
		this.hasUnsavedChanges = false;
	}

	handleEditTitle(name: string) {
		this.activeRoster.name = name;
		this.hasUnsavedChanges = true;
	}

	async handleSaveClick() {
		if (this.rosters == null) {
			return;
		}
		this.isLoading = true;
		try {
			await rostersAPI.saveAll(this.rosters);
			await this.fetchRosters();
			this.$snackbar.openInfo(this.$t("snackbar.save_success"));
		} catch (error) {
			this.handleAPIError(error);
		}
		this.isLoading = false;
	}

	async handleAddRosterClick() {
		if (!this.rosters) {
			return;
		}

		this.isLoading = true;
		try {
			const rosterID = await rostersAPI.createRoster(this.currentTeam.id);
			await this.fetchRosters();
			this.activeRosterId = rosterID;
		} catch (error) {
			this.handleAPIError(error);
		}
		this.isLoading = false;
	}

	async handleRemoveRosterClick() {
		if (!this.rosters) {
			return;
		}

		const activeRosterIndex = this.rosters.findIndex(x => x.id == this.activeRosterId);

		if (!window.confirm(this.$t("roster.alert_remove").toString())) {
			return;
		}

		this.isLoading = true;
		try {
			await rostersAPI.deleteRoster(this.activeRoster.id);
			await this.fetchRosters();
		} catch (error) {
			this.handleAPIError(error);
		}
		this.isLoading = false;

		if (this.rosters.length > 0) {
			const nextRosterIndex = activeRosterIndex >= this.rosters.length ? activeRosterIndex - 1 : activeRosterIndex;
			this.activeRosterId = this.rosters[nextRosterIndex].id;
		}
		else {
			this.activeRosterId = null;
		}

	}

	handleMoveCharacterInRoster(character: any, previousRosterID: any, nextRosterID: any, role: string) {
		if (this.rosters == null) {
			return;
		}

		this.handleRemoveCharacterFromRoster(character, previousRosterID);
		this.handleCopyCharacterToRoster(character, nextRosterID, role);

		this.hasUnsavedChanges = true;
	}

	handleCopyCharacterToRoster(character: any, rosterID: any, role: string) {
		if (this.rosters == null) {
			return;
		}

		const rosterToAddCharacterTo = this.rosters.find(r => r.id === rosterID);

		if (rosterToAddCharacterTo.characters.length >= this.currentTeam.premium.nbMaximumCharactersPerRoster) {
			return;
		}

		const charactersSet = rosterToAddCharacterTo.characters as any[];
		rosterToAddCharacterTo.characters = [...charactersSet.filter(c => c.id !== character.id), {
			...character,
			role: role
		}];

		this.hasUnsavedChanges = true;
	}

	handleRemoveCharacterFromRoster(character: any, rosterID: any) {
		if (this.rosters == null) {
			return;
		}
		const rosterToDeleteCharacterFrom = this.rosters.find(r => r.id === rosterID);
		const charactersSet = rosterToDeleteCharacterFrom.characters as any[];
		rosterToDeleteCharacterFrom.characters = charactersSet.filter(c => c.id !== character.id);

		this.hasUnsavedChanges = true;
	}

	handleCharacterContextMenu(character: any, rosterID: any, ev: Event) {
		if (this.rosters == null) {
			return;
		}
		const rosterToModifyCharacterFrom = this.rosters.find(r => r.id === rosterID);
		const characterToModify = rosterToModifyCharacterFrom.characters.find((x: any) => x.id == character.id);
		const roleInfo = roles.find(x => x.name == characterToModify.role);
		if (roleInfo == null) {
			return;
		}

		const nonEmptyRoles = roleInfo.subroles.filter(x => x != "");
		if (nonEmptyRoles.length == 0) {
			return;
		}

		const subRoleIndex = nonEmptyRoles.findIndex(x => x == characterToModify.subRole);

		const numberOfNonEmptyRoles = nonEmptyRoles.length;

		characterToModify.subRole = nonEmptyRoles[(subRoleIndex + 1) % numberOfNonEmptyRoles];

		this.hasUnsavedChanges = true;
	}
}
